/**
 * The split a plan gate offers when the plan spans repositories (YAG-1058):
 * the gate's `payload.split`, as the one-attempt lane's plan gate writes it
 * (`openPlanGateActivity`), read into the rows the split panel pre-fills.
 */
import type { WorkItemGate } from "@yagni/shared";
import type { SplitDraftRow } from "../work/pageActs.js";

export interface SplitOffer {
  /** "This change spans 2 repositories: gpx/api, gpx/web." */
  line: string;
  repositories: Array<{
    repoId: string;
    fullName: string;
    order: number;
    summary: string;
    /** Documentation built beside the code, never after it (YAG-1067). */
    beside?: boolean;
  }>;
  /** One row per repository, in build order, each naming its repository. */
  children: SplitDraftRow[];
}

function text(value: unknown): string {
  return typeof value === "string" ? value : "";
}

/** The gate's split offer, or null for a gate that offers none. */
export function readSplitOffer(gate: WorkItemGate | null | undefined): SplitOffer | null {
  const raw = gate?.payload?.split;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const offer = raw as Record<string, unknown>;
  const repositories = (Array.isArray(offer.repositories) ? offer.repositories : [])
    .flatMap((entry) => {
      const repo = entry as Record<string, unknown> | null;
      if (!repo || typeof repo.repoId !== "string" || typeof repo.fullName !== "string") return [];
      return [
        {
          repoId: repo.repoId,
          fullName: repo.fullName,
          order: typeof repo.order === "number" ? repo.order : 0,
          summary: text(repo.summary),
          ...(repo.beside === true ? { beside: true } : {}),
        },
      ];
    })
    .sort((a, b) => a.order - b.order);
  const children = (Array.isArray(offer.children) ? offer.children : []).flatMap((entry) => {
    const child = entry as Record<string, unknown> | null;
    if (!child || typeof child.title !== "string" || typeof child.repoId !== "string") return [];
    const steps = Array.isArray(child.steps)
      ? child.steps.filter((s): s is string => typeof s === "string")
      : [];
    return [{ title: child.title, steps: steps.join("\n"), repoId: child.repoId }];
  });
  if (children.length < 2 || repositories.length < 2) return null;
  return {
    line: text(offer.line) || `This change spans ${repositories.length} repositories.`,
    repositories,
    children,
  };
}

/**
 * How the split's tickets build: "3 tickets, built in order", or with
 * documentation, whose ticket is built beside the code (YAG-1067), "2
 * tickets: 1 built in order, documentation beside them". The offer's heading
 * and its button both say it, from here, so they never disagree.
 */
export function splitTicketsFact(count: number, beside = 0): string {
  return beside > 0
    ? `${count} tickets: ${count - beside} built in order, documentation beside them`
    : `${count} tickets, built in order`;
}

/** The offer's fact, the heading over its repositories. */
export function splitOfferFact(offer: SplitOffer): string {
  const beside = offer.repositories.filter((repo) => repo.beside).length;
  return splitTicketsFact(offer.children.length, beside);
}

/** The split's button. */
export function splitIntoLabel(count: number, beside = 0): string {
  return `Split into ${splitTicketsFact(count, beside)}`;
}

/** Said under the offer: the split is a choice, and approving keeps the one ticket. */
export const SPLIT_OPTIONAL_LINE = "Optional. Approve the plan as it is to keep it as one ticket.";
